/**
 * City Access Domain
 * Manages permanent city Influence through the existing city-access protocol.
 * Investments use team approval; first presence permits service to/from the city.
 *
 * Standard domain pattern: TYPES → STORE → HANDLERS → SERVICE METHODS
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cityInfluence, type Airport } from './airports';
import { sendGatedCommand } from './command-gate';
import { handleAirportUpdate, useAirportsStore } from './airports';
import { getInvestmentConnectionOrigins, type Segment } from './segments';
import { handleTeamUpdate } from './teams';
import type { Team } from './teams';
import { OpCode, MessageType } from '../constants/op-codes';
import { calculateDistance } from '../constants/server-manifest';
import { cities, cityName } from '../data/cities';
import i18n from '../i18n/config';
import { getCurrentCohortId, getCurrentTeam } from './current-team';
import { showErrorToast } from './toasts';
import { refusalKey, refusalText, type OrderBlock, type OrderRefusal } from './order-block';

// ==================== TYPES ====================

/** Payload for the unlocked cities state sync (sent on join) */
interface UnlockedCitiesStatePayload {
  type: string;
  cohort_id: string;
  unlocked_city_ids: number[];
}

/** Server origin for unlock_city_success — drives client-only UX (e.g. team announcement). */
type UnlockCitySource = 'city_access' | 'step-2-hq-selection';

/** Payload for a successful city unlock broadcast */
interface UnlockCitySuccessPayload {
  type: string;
  city_id: number;
  team: Team;
  cohort_id: string;
  cost: number;
  sender_user_id: string;
  /** The teammates whose approval carried it, the sender among them. */
  approvers: string[];
  airport?: Airport;
  global_hq_set?: boolean;
  /** Present on newer servers; omit on older builds. */
  unlock_source?: UnlockCitySource;
}

// ==================== STORE ====================

interface CityAccessState {
  /** Set of city IDs unlocked by the player's team */
  unlockedCityIds: Set<number>;
  /** City ID currently being unlocked (optimistic UI feedback) */
  pendingUnlockCityId: number | null;
  /** Brief team-wide “city unlocked” overlay (cosign / influence only). */
  teamUnlockOverlayCityId: number | null;

  // Store methods
  setUnlockedCityIds: (ids: number[]) => void;
  addUnlockedCity: (cityId: number) => void;
  setPendingUnlockCityId: (cityId: number | null) => void;
  showTeamUnlockOverlay: (cityId: number) => void;
  dismissTeamUnlockOverlay: () => void;
  reset: () => void;
}

const initialState = {
  unlockedCityIds: new Set<number>(),
  pendingUnlockCityId: null as number | null,
  teamUnlockOverlayCityId: null as number | null,
};

export const useCityAccessStore = create<CityAccessState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setUnlockedCityIds: (ids: number[]) => {
        set({ unlockedCityIds: new Set(ids) }, false, 'setUnlockedCityIds');
      },

      addUnlockedCity: (cityId: number) => {
        const updated = new Set(get().unlockedCityIds);
        updated.add(cityId);
        set({ unlockedCityIds: updated, pendingUnlockCityId: null }, false, 'addUnlockedCity');
      },

      setPendingUnlockCityId: (cityId: number | null) => {
        set({ pendingUnlockCityId: cityId }, false, 'setPendingUnlockCityId');
      },

      showTeamUnlockOverlay: (cityId: number) => {
        set({ teamUnlockOverlayCityId: cityId }, false, 'showTeamUnlockOverlay');
      },

      dismissTeamUnlockOverlay: () => {
        set({ teamUnlockOverlayCityId: null }, false, 'dismissTeamUnlockOverlay');
      },

      reset: () => {
        set({
          unlockedCityIds: new Set<number>(),
          pendingUnlockCityId: null,
          teamUnlockOverlayCityId: null,
        }, false, 'reset');
      },
    }),
    { name: 'city-access-store' }
  )
);

// ==================== REFUSALS ====================

/**
 * The reasons the server refuses an investment for, where a player can meet
 * them — city-access.go and spending-lock.go send each as a code beside the
 * English — by the key that says each in the player's language. `approval`
 * carries the approvals the investment needed and the teammates who were
 * giving theirs; `out_of_reach` is said as the city panel says it before an
 * investment. A Go test reads this table (city_access_refusal_test.go), so the
 * two sides name the same codes.
 */
const CITY_ACCESS_REFUSALS = {
  approval: 'cityAccess.refusal.approval',
  influence_locked: 'common.influenceLocked',
  investment_changed: 'cityAccess.refusal.investmentChanged',
  no_influence: 'cityAccess.refusal.noInfluence',
  out_of_reach: 'segment.tooFarFromNetworkRoot',
} as const;

/**
 * What a refused investment tells the player: the key that says its reason,
 * with its figures. Null when the server sent no reason the client knows — a
 * failure no player meets in play, or a code from a newer server — or a reason
 * that lost its figures, and the server's words stand.
 */
export function readCityAccessRefusal({ reason, need, have }: OrderRefusal): OrderBlock | null {
  const key = refusalKey(CITY_ACCESS_REFUSALS, reason);
  if (key === undefined) return null;
  if (reason !== 'approval') return { key };
  if (typeof need !== 'number' || typeof have !== 'number') return null;
  return { key, values: { need, have } };
}

/**
 * Which cities a first investment can reach, as the server holds it
 * (city-access.go, isCityWithinReach): a city is within reach while one of
 * yours in your network's reach — your headquarters (`hqId`), your Bases, and
 * the cities your flown routes reach from them within the network's hops less
 * one (getInvestmentConnectionOrigins), each where you hold Influence — lies
 * within `rangeKm` of it, your aircraft's longest. city-reach-cases.json
 * holds the two to the same cases (city-access.test.ts, city_reach_test.go).
 * Worked out once for the headquarters, airports and routes given, and then
 * asked of each city: the globe's marks ask it of every one. Before your
 * headquarters there is no reach to ask about — any city can be the one — so
 * the marks do not.
 */
export function cityReach(
  teamId: number,
  hqId: number,
  airports: Record<number, Airport>,
  segments: Record<number, Segment>,
  rangeKm: number,
): (cityId: number) => boolean {
  const origins = [...getInvestmentConnectionOrigins(teamId, hqId, airports, segments)]
    .filter((id) => cityInfluence(airports[id], teamId) > 0)
    .flatMap((id) => cities.getById(id) ?? []);
  return (cityId) => {
    const city = cities.getById(cityId);
    return !!city && origins.some((origin) => origin.id !== cityId && calculateDistance(origin.lat, origin.lon, city.lat, city.lon) <= rangeKm);
  };
}

// ==================== HANDLERS ====================

/**
 * Handle initial unlocked cities state sync when player joins the match.
 * Receives the full set of city IDs unlocked by the player's team.
 */
export function handleUnlockedCitiesState(data: UnlockedCitiesStatePayload) {
  console.log('[CityAccess] Received unlocked cities state:', data.unlocked_city_ids?.length, 'cities');
  const store = useCityAccessStore.getState();
  store.setUnlockedCityIds(data.unlocked_city_ids || []);
}

/**
 * Handle successful city unlock broadcast.
 * Updates the unlocked cities set and team cash for all clients.
 */
export function handleUnlockCitySuccess(data: UnlockCitySuccessPayload) {
  console.log('[CityAccess] City unlocked:', data.city_id, 'by team', data.cohort_id, 'cost:', data.cost);
  const viewerCohortId = getCurrentCohortId();

  // This store tracks only the current player's team unlocks.
  if (viewerCohortId && viewerCohortId === data.cohort_id) {
    useCityAccessStore.getState().addUnlockedCity(data.city_id);
  }

  // Update team data (cash deducted)
  if (data.team && data.cohort_id) {
    handleTeamUpdate(data.cohort_id, data.team);
  }

  // Server includes the updated airport when city unlock succeeds (e.g. first unlocked city sets HQ).
  if (data.airport && typeof data.airport.id === 'number') {
    handleAirportUpdate(data.airport);
  }

  // Team-wide overlay: cosign/influence unlocks only — not scripted HQ vote (step-2-hq-selection).
  if (
    viewerCohortId &&
    viewerCohortId === data.cohort_id &&
    data.unlock_source === 'city_access' &&
    (data.airport?.influence_by_team?.[data.team.team_id] ?? 1) === 1
  ) {
    useCityAccessStore.getState().showTeamUnlockOverlay(data.city_id);
  }
}

/**
 * Handle a refused city investment: the press is over, so the button leaves
 * its sent state, and a toast names the city and says why — in the player's
 * language when the server sent a reason the client knows
 * (readCityAccessRefusal), else in the server's words. The vote stays as it
 * was — the teammates are still on the button.
 */
export function handleUnlockCityFailed(data: OrderRefusal) {
  console.error('[CityAccess] ❌ Unlock city failed:', data.reason ?? '', data.error);
  const store = useCityAccessStore.getState();
  const code = store.pendingUnlockCityId !== null ? cities.getById(store.pendingUnlockCityId)?.code : undefined;
  store.setPendingUnlockCityId(null);
  showErrorToast(
    code ? i18n.t('fourX.investFailed', { city: cityName(code) }) : i18n.t('fourX.investFailedHere'),
    refusalText(readCityAccessRefusal(data), data),
  );
}

// ==================== SERVICE METHODS ====================

/**
 * Request to unlock a city for the current player's team.
 * Server validates funds, co-sign state, and that the city isn't already unlocked.
 * @param cityId - The city ID to unlock
 */
export async function unlockCity(cityId: number): Promise<void> {
  const store = useCityAccessStore.getState();

  // Optimistic: mark as pending
  store.setPendingUnlockCityId(cityId);

  const team = getCurrentTeam();
  const expectedInfluence = team ? useAirportsStore.getState().airports[cityId]?.influence_by_team?.[team.team_id] ?? 0 : 0;
  const message = JSON.stringify({
    type: MessageType.MessageTypeUnlockCity,
    city_id: cityId,
    expected_influence: expectedInfluence,
    request_id: crypto.randomUUID(),
  });

  await sendGatedCommand(OpCode.OpCodeUnlockCity, message, {
    endWait: () => store.setPendingUnlockCityId(null),
    notSent: handleUnlockCityFailed,
  });
}

// ==================== THE PLAYER'S ACTS ====================

/** The team's new city's moment closes: its Continue, the scrim, or its clock run out. */
export function dismissCityUnlockMoment(): void {
  useCityAccessStore.getState().dismissTeamUnlockOverlay();
}
